export const INVALID_DATE_OBJECT_STRING_SUFFIX = "|INVALID_DATE_OBJECT";

/** *
 * Transforms a `string` of format `DD/MM/YYYY` to a `Date`
 * @param value
 */
export const parseDate = (value: string): Date => {
  const [day, month, year] = value.split("/").map((p) => parseInt(p, 10));
  return new Date(Date.UTC(year, month - 1, day));
};

export const getDateFromValue = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value !== "string" && typeof value !== "number") {
    return undefined;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

/** *
 * Transforms a `Date` to a 'string' of format `DD/MM/YYYY`
 * @param value
 */
export const convertDateToStringDDMMYYYY = (value: Date): string => {
  const day = value.getDate() < 10 ? `0${value.getDate()}` : value.getDate();
  const month = value.getMonth() + 1 < 10 ? `0${value.getMonth() + 1}` : value.getMonth() + 1;
  const year = value.getFullYear();
  return `${day}/${month}/${year}`;
};

/** *
 * Converts a date to a string of format YYYY-MM-DD
 * @param value
 */
export const convertDateToStringYYYYMMDD = (value: Date | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const dateUTC = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  return dateUTC.toISOString().slice(0, 10);
};

/** *
 * Returns the picked calendar day at 00:00:00 UTC. Use for date-only values that the API takes
 * as a date-time, so neither the local time-of-day nor the timezone offset leaks into the day.
 * @param value
 */
export const convertDateToUtcStartOfDay = (value: Date): Date =>
  new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));

/** *
 * Parses a date-only string of format YYYY-MM-DD as a local date.
 * This avoids timezone shifts caused by `new Date("YYYY-MM-DD")`, which is interpreted as UTC.
 * @param value
 */
export const parseDateOnlyStringYYYYMMDD = (value: string): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return undefined;
  }

  const [, year, month, day] = match;
  const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

  return parsedDate.getFullYear() === Number(year) &&
    parsedDate.getMonth() === Number(month) - 1 &&
    parsedDate.getDate() === Number(day)
    ? parsedDate
    : undefined;
};

/** *
 * Checks if a string is a valid date and fits one of this 2 formats `dd/MM/yyyy` or `d/M/yyyy`
 * @param value
 */
export const isValidDate = (value: string | null | undefined): boolean => {
  if (!value) {
    return true;
  }

  const [day, month, year] = value.split("/");
  if (
    !day ||
    day.length < 1 ||
    day.length > 2 ||
    !month ||
    month.length < 1 ||
    month.length > 2 ||
    !year ||
    year.length !== 4
  ) {
    return false;
  }

  const [dayInt, monthInt, yearInt] = [day, month, year].map((p) => parseInt(p, 10));
  const date = new Date(yearInt, monthInt - 1, dayInt);
  return date.getDate() === dayInt && date.getMonth() + 1 === monthInt && date.getFullYear() === yearInt;
};

/** *
 *  Checks if a string is a valid date and transforms it to a `Date`
 * @param value
 */
export const validateAndParseDate = (value: string): Date => {
  if (!isValidDate(value)) {
    return new Date("Invalid Date");
  }

  return parseDate(value);
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

const getUtcStartOfDay = (date: Date): number => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/**
 * Compares the two values' UTC calendar days.
 *
 * A date-only value from the API is parsed as *local* midnight (see `maybeJsonDateStringParser`), so
 * pass both sides through `convertDateToUtcStartOfDay` first — including the reference date, whose
 * UTC day otherwise differs from the user's local day for part of every day (evening at a negative
 * offset, morning at a positive one).
 */
export const isPastDateByDay = (value: Date, referenceDate = new Date()): boolean => {
  return getUtcStartOfDay(value) < getUtcStartOfDay(referenceDate);
};

export const formatDateWithRecency = (date: Date): string => {
  const currentDate = new Date();
  if (isSameDay(currentDate, date)) {
    return "Today";
  }

  const yesterdayDate = new Date(currentDate);
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  if (isSameDay(yesterdayDate, date)) {
    return "Yesterday";
  }

  return convertDateToStringDDMMYYYY(date);
};

export const calculateApproximateYearsAndMonthsBetween = (
  startDate: Date,
  endDate: Date
): { years: number; months: number } => {
  const secondsInAMonth = 30 * 24 * 60 * 60; // Approximate
  const secondsInAYear = 365 * 24 * 60 * 60; // Approximate

  const dateDifferenceInSeconds = (endDate.getTime() - startDate.getTime()) / 1000;

  const years = Math.floor(dateDifferenceInSeconds / secondsInAYear);
  const remainingSeconds = dateDifferenceInSeconds % secondsInAYear;
  const months = Math.floor(remainingSeconds / secondsInAMonth);

  return { years, months };
};

export const addDaysToDate = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const convertDaysToYearsMonthsWeeksAndDays = (days: number): string => {
  let absoluteDays = Math.abs(days);
  const years = Math.floor(absoluteDays / 365);
  absoluteDays %= 365;

  const months = Math.floor(absoluteDays / 30);
  absoluteDays %= 30;

  const weeks = Math.floor(absoluteDays / 7);
  absoluteDays %= 7;

  // Add plural 's' when > 1
  const formatUnit = (value: number, unit: string): string => (value === 1 ? `${value} ${unit}` : `${value} ${unit}s`);

  return [
    years > 0 ? formatUnit(years, "year") : undefined,
    months > 0 ? formatUnit(months, "month") : undefined,
    weeks > 0 ? formatUnit(weeks, "week") : undefined,
    absoluteDays > 0 ? formatUnit(absoluteDays, "day") : undefined,
  ]
    .filter(Boolean)
    .join(", ");
};

export type DeadlineColourClass = "Low" | "Medium" | "High" | "VeryHigh";

export const getDeadlineColourClass = (date?: Date): DeadlineColourClass => {
  if (!date) return "Low";
  const days = Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return "VeryHigh";
  if (days <= 7) return "High";
  if (days <= 30) return "Medium";
  return "Low";
};

export const getProbabilityColourClass = (probability: number | null | undefined): DeadlineColourClass => {
  if (probability === null || probability === undefined) return "Low";
  if (probability >= 75) return "Low";
  if (probability >= 50) return "Medium";
  if (probability >= 25) return "High";
  return "VeryHigh";
};

export const calculateCalanderUnit = (value: number): string => {
  if (value % 365 === 0) return "years";
  if (value % 30 === 0) return "months";
  if (value % 7 === 0) return "weeks";
  return "days";
};
